import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { registrarAcceso } from "../utils/accesos";

const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }

// Cierre de sesión automático: cada usuario debe volver a loguearse si pasan
// 20 horas desde su último inicio de sesión, sin importar el rol.
const SESION_MAX_MS = 20 * 60 * 60 * 1000;
const claveSesion = (uid) => `sesionInicio_${uid}`;

// A diferencia de logout() (botón "Cerrar sesión", transición suave dentro de
// la misma app), esto se usa cuando la sesión expiró sola: recarga la página
// por completo para que de paso el navegador jale la versión más reciente
// publicada, no solo la pantalla de login de la app ya cargada en la pestaña.
async function expirarSesion(uid) {
  localStorage.removeItem(claveSesion(uid));
  await signOut(auth);
  window.location.reload();
}

// Bootstrap único para las cuentas que ya existían antes de que la autorización
// viviera en Firestore (colección usuarios_autorizados). La primera vez que
// cada una de estas cuentas inicia sesión, se crea su documento en
// usuarios_autorizados automáticamente; de ahí en adelante ese documento manda
// y este mapa deja de usarse para ella. Cuentas nuevas se dan de alta desde la
// pantalla de Usuarios, no aquí.
const LEGACY_FALLBACK = {
  "ari@goam.com.mx": { rol: "dueno", nombre: "Ari" },
  "aricybula@gmail.com": { rol: "dueno", nombre: "Ari" },
  "elizabeth@goam.com.mx": { rol: "admin", nombre: "Elizabeth" },
  "puntanorte@goam.com.mx": { rol: "gerente", nombre: "Gerente PN", unidad: "punta_norte" },
  "aguilas@goam.com.mx": { rol: "gerente", nombre: "Gerente Ag", unidad: "aguilas" }
};

// Busca (y si hace falta, crea vía el fallback legacy) la autorización de un
// correo en Firestore. Devuelve null si el correo no tiene acceso al sistema.
export async function obtenerAutorizacion(email) {
  const emailLower = (email || "").toLowerCase();
  if (!emailLower) return null;
  const ref = doc(db, "usuarios_autorizados", emailLower);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const datos = snap.data();
    return datos.activo ? datos : null;
  }
  const legacy = LEGACY_FALLBACK[emailLower];
  if (!legacy) return null;
  const datos = { email: emailLower, activo: true, unidad: null, unidades: [], ...legacy, creadoEn: new Date() };
  await setDoc(ref, datos);
  return datos;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const rol = userProfile?.rol || null;
  const unidadAsignada = userProfile?.unidad || null;
  const unidadesAsignadas = userProfile?.unidades?.length ? userProfile.unidades : (unidadAsignada ? [unidadAsignada] : []);
  const isAdmin = rol === "dueno" || rol === "admin";

  async function sincronizarPerfil(user, autorizacion) {
    const perfil = {
      nombre: autorizacion.nombre || user.displayName || user.email,
      email: user.email?.toLowerCase() || "",
      rol: autorizacion.rol,
      unidad: autorizacion.unidad || null,
      unidades: autorizacion.unidades || [],
      modulosPermitidos: autorizacion.modulosPermitidos || null,
      modulosSoloLectura: autorizacion.modulosSoloLectura || [],
      activo: true
    };
    await setDoc(doc(db, "usuarios", user.uid), { ...perfil, actualizadoEn: new Date() }, { merge: true });
    setUserProfile(perfil);
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const autorizacion = await obtenerAutorizacion(user.email);
          const key = claveSesion(user.uid);
          const inicio = localStorage.getItem(key);
          const expirada = inicio && Date.now() - parseInt(inicio, 10) > SESION_MAX_MS;
          if (expirada) {
            await expirarSesion(user.uid);
            return;
          }
          if (!autorizacion) {
            localStorage.removeItem(key);
            await signOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
          } else {
            if (!inicio) {
              localStorage.setItem(key, String(Date.now()));
              registrarAcceso({ email: user.email, nombre: autorizacion.nombre || user.displayName });
            }
            setCurrentUser(user);
            await sincronizarPerfil(user, autorizacion);
          }
        } catch (err) {
          console.error("Error verificando autorización:", err);
          await signOut(auth);
          setCurrentUser(null);
          setUserProfile(null);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Revisa cada 5 min por si la pestaña se queda abierta más de 20 hrs sin recargar.
  useEffect(() => {
    if (!currentUser) return;
    const key = claveSesion(currentUser.uid);
    const revisar = () => {
      const inicio = localStorage.getItem(key);
      if (inicio && Date.now() - parseInt(inicio, 10) > SESION_MAX_MS) {
        expirarSesion(currentUser.uid);
      }
    };
    const intervalo = setInterval(revisar, 5 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, [currentUser]);

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    // Popup funciona en todos los casos — desktop, Android, iOS
    // Safari iOS: el popup debe venir de un click directo del usuario (ya es así)
    return signInWithPopup(auth, provider);
  }

  async function logout() {
    if (currentUser) localStorage.removeItem(claveSesion(currentUser.uid));
    setUserProfile(null);
    return signOut(auth);
  }

  const value = { currentUser, userProfile, rol, unidadAsignada, unidadesAsignadas, isAdmin, login, loginWithGoogle, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
